import { createUseStyles } from "react-jss";
import { theme } from "$utils/theme/bataillog";

import { EdbStatus, PreparationOrderStatus } from "$types";
import { getEdbStatusLabel, getEdbStatusStep } from "$utils/typeConstants";
import Step from "./Step";

type Props = {
  ops: any[];
  datePrevisionnelle: string;
  edb?: any;
  hasProduct?: boolean;
};

const getMaxStepFromOps = (ops: any[], edb?: any): number => {
  const activeOps = ops?.filter(
    (op: any) => op.status !== PreparationOrderStatus.CANCELLED
  );

  if (!activeOps || activeOps.length === 0) {
    return edb?.status ? getEdbStatusStep(edb.status) : 0;
  }

  const maxOpStatus = Math.max(...activeOps.map((op: any) => op.status || 0));

  const statusToStepMap: { [key: number]: number } = {
    [PreparationOrderStatus.CREATED]: 0, // CREATED -> CART_VALIDATED (step 0)
    [PreparationOrderStatus.IN_PROGRESS]: 1, // IN_PROGRESS -> PREP_IN_PROGRESS (step 1)
    [PreparationOrderStatus.PREPARED]: 2, // PREPARED -> PREP_AVAILABLE (step 2)
    [PreparationOrderStatus.TO_SHIP]: 2, // TO_SHIP -> PREP_AVAILABLE (step 2)
    [PreparationOrderStatus.SHIPPED]: 3, // SHIPPED -> SHIPPED (step 3)
    [PreparationOrderStatus.DELIVERED]: 4, // DELIVERED -> DELIVERED (step 4)
    [PreparationOrderStatus.COLLECTED]: 5, // COLLECTED -> COLLECTED (step 5)
  };

  return statusToStepMap[maxOpStatus] ?? 0;
};

function Timeline({ ops, datePrevisionnelle, edb, hasProduct }: Props) {
  const classes = useStyles();
  const currentStep = getMaxStepFromOps(ops, edb);
  const nextStepStatus = currentStep + 1;
  const isRed = new Date(datePrevisionnelle) < new Date();

  return (
    <div className={classes.tunel}>
      <ul className={classes.steps}>
        {Object.keys(EdbStatus)
          .filter(
            (key) =>
              EdbStatus[key] !== EdbStatus.REFUSED &&
              EdbStatus[key] !== EdbStatus.WAITING
          )
          .map((key, index) => (
            <Step
              hasProduct={hasProduct}
              edb={edb || undefined}
              key={key}
              label={getEdbStatusLabel(EdbStatus[key])}
              ops={ops}
              step={getEdbStatusStep(EdbStatus[key])}
              currentEdbStep={currentStep}
              isRed={nextStepStatus === index - 1 && isRed}
            />
          ))}
      </ul>
    </div>
  );
}

const useStyles = createUseStyles(() => ({
  tunel: {},
  steps: {
    display: "flex",
    padding: 0,
    alignItems: "flex-start",
    justifyContent: "center",
    counterReset: "step",
    margin: "12px 0 8px",
    "& .step": {
      color: `${theme.color.gray5}`,
      fontSize: 15,
      "&:before": {
        counterIncrement: "step",
        content: "counter(step)",
        width: 32,
        height: 32,
        justifyContent: "center",
        alignItems: "center",
        display: "flex",
        border: `solid 1px ${theme.color.gray3}`,
        color: `${theme.color.gray5}`,
        borderRadius: "100%",
        fontSize: 18,
      },
    },
    "& .active.step": {
      color: `${theme.color.blueLight}`,
      "&:before": {
        border: `solid 1px ${theme.color.blueLight}`,
        color: "#fff",
        background: `${theme.color.blueLight}`,
      },
    },
    "& .semi.step": {
      color: `${theme.color.blueLight}`,
      "&:before": {
        color: "#fff",
        background:
          "linear-gradient(to right, #457dd9 50%, #DFE1E6 50%) !important",
        borderColor: "#DFE1E6 !important",
      },
    },
    "& .red.step": {
      color: `${theme.color.red}`,
      "&:before": {
        color: "#fff",
        background: `${theme.color.red}  !important`,
      },
    },
  },
}));

export default Timeline;
